import React from 'react';

// Outbound URL templates — the single source of truth. Disease.js, WhatsNew.js
// and DiseaseDetail.js import from here.
export const OMIM_URL = 'http://www.omim.org/entry/';
export const GENE_URL = 'https://www.ncbi.nlm.nih.gov/datasets/gene/';
export const CLINGEN_URL = 'https://search.clinicalgenome.org/kb/genes/HGNC:';

// `onOutbound` is an optional no-arg callback fired on click. The detail page
// passes a handler that fires reference_outbound (it owns the target_resource
// vocabulary); the /disease table passes nothing (no tracking this iteration).
const fire = (onOutbound) => () => { if (onOutbound) onOutbound(); };

// Every target="_blank" link says so to assistive tech. Bootstrap's
// visually-hidden class keeps it off the screen.
const NewTab = () => <span className="visually-hidden"> (opens in a new tab)</span>;

// Splits one of the free-text list fields. Same rule as DataHelper.split on
// the server: , or ;, trimmed, blanks dropped.
const tokens = (s) => (s ? s.split(/[,;]/).map(t => t.trim()).filter(Boolean) : []);

// The McKusick (OMIM) field is a free-text list. Mirrors the McKusick
// handling in Helper/DiseasesHelper.cs (split on , or ; via DataHelper.split,
// then trimmed, blanks dropped); that file carries a pointer back here. Every
// client reading of the field goes through this function so the count, the
// links and the JSON-LD sameAs never disagree.
export function omimNumbers(mckusick) {
  return tokens(mckusick);
}

// Links into the references page. Also used by portal/Disease.js, whose
// data shape is a bare reference string rather than an id.
export function referenceHref(id) {
  return './references#' + id;
}

// The per-gene page's URL key. Mirrors GeneResolution.CanonicalKey in
// Helper/GeneResolution.cs -- symbol1, else the first alias token, else the
// id; that file carries a pointer back here. Every client-side link into
// /disease/<key> goes through this, so a listing and the page it lands on can
// never disagree about the key.
export function geneKey(disease) {
  if (!disease) return null;
  const primary = (disease.symbol1 || '').trim();
  if (primary) return primary;
  const alias = tokens(disease.symbol2)[0];
  if (alias) return alias;
  return disease.id != null ? String(disease.id) : null;
}

// Null when the row has no usable key at all, so callers render plain text
// rather than a link that cannot resolve.
export function geneHref(disease) {
  const key = geneKey(disease);
  return key === null ? null : '/disease/' + encodeURIComponent(key);
}

// A disease's displayed name, linked to its own page. INTERNAL navigation, so
// no onOutbound: firing an outbound event here would mislabel it, the same
// reasoning ReferenceList below is built on.
export function GeneName({ disease, children }) {
  const href = geneHref(disease);
  return href ? <a href={href}>{children}</a> : <>{children}</>;
}

export function OmimLinks({ mckusick, onOutbound }) {
  const numbers = omimNumbers(mckusick);
  if (!numbers.length) return null;
  return numbers.map((omim, i) => (
    <a key={i} className="tableLinkList" target="_blank" rel="noreferrer"
       href={OMIM_URL + omim.replace(/ /g, '')} onClick={fire(onOutbound)}>{omim}<NewTab /></a>
  ));
}

// `label` defaults to the bracketed form the /disease table has always used;
// the detail page passes the resource name instead.
export function GeneLink({ locusLink, onOutbound, label = '[Gene]' }) {
  if (!locusLink) return null;
  return (
    <span> <a target="_blank" rel="noreferrer" href={GENE_URL + locusLink}
       onClick={fire(onOutbound)}>{label}<NewTab /></a></span>
  );
}

export function ClinGenLink({ clinGen, onOutbound, label = '[ClinGen]' }) {
  if (!clinGen) return null;
  return (
    <span> <a target="_blank" rel="noreferrer" href={CLINGEN_URL + clinGen}
       onClick={fire(onOutbound)}>{label}<NewTab /></a></span>
  );
}

// No onOutbound: these are INTERNAL links to /references, not outbound
// citations. Firing reference_outbound here would mislabel internal navigation.
// A reference with no id (the view is a left join) is shown as text.
export function ReferenceList({ references }) {
  if (!references) return null;
  return references.map((ref, i) => ref.referenceId == null
    ? <span key={i} className="tableLinkList">{ref.reference}</span>
    : <a key={i} className="tableLinkList" href={referenceHref(ref.referenceId)}>{ref.reference}</a>
  );
}
